import { Box } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PickTaskFilterBar } from './PickTaskFilterBar';
import { PickTaskListHeader } from './PickTaskListHeader';
import { PickTaskTable } from './PickTaskTable';
import { usePickTaskListState } from './usePickTaskListState';
import { usePickTaskReports } from './usePickTaskReports';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { SelectMenu } from '../../../components/atoms/SelectMenu';
import { AutoRefresh, TablePagination } from '../../../components/molecules/Pagination';
import { ReportTabs, SaveReportDialog } from '../../../components/molecules/ReportTabs';
import { ColumnSelection } from '../../../components/templates/ColumnSelection';
import { TabHandler } from '../../../handlers/TabHandler';
import { usePinnedReports } from '../../../hooks/usePinnedReports';

export const PickTaskList: React.FC = () => {
  const navigate = useNavigate();
  const [columnsOpen, setColumnsOpen] = useState(false);
  TabHandler('Pick Tasks | CMS');

  const { pinnedCodes, setPinnedCodes, defaultTabIndex, reorderTabs } =
    usePinnedReports('PickTaskList');

  const state = usePickTaskListState();
  const {
    filters,
    sortModel,
    page,
    setPage,
    pageSize,
    setPageSize,
    columnSelection,
    setColumnSelection,
    autoRefreshInterval,
    setAutoRefreshInterval,
    fetchDurationMs,
    selectedIds,
    pageRows,
    total,
  } = state;

  const reports = usePickTaskReports({
    currentFilters: filters,
    sortModel,
    pageSize,
    currentColumnSelection: columnSelection,
    onApply: state.applyReportSnapshot,
  });

  return (
    <>
      <PickTaskListHeader total={total} onColumnsClick={() => setColumnsOpen(true)} />
      <PageContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <ReportTabs
            activeReport={reports.activeReport}
            reports={reports.savedReports}
            onReportChange={reports.handleReportChange}
            onSave={() => reports.setSaveDialogOpen(true)}
            defaultTabLabel={reports.defaultTabLabel}
            onSetDefault={reports.handleSetDefault}
            pinnedCodes={pinnedCodes}
            onPinnedCodesChange={setPinnedCodes}
            defaultTabIndex={defaultTabIndex}
            onReorderTabs={reorderTabs}
          />
          <PickTaskFilterBar
            filters={filters}
            onChange={state.onFiltersChange}
            onSearch={state.onSearch}
            onReset={state.onReset}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '20px',
              py: '3px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontSize: '12px',
                color: 'text.secondary',
              }}
            >
              {total > 0 && (
                <span>
                  Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of{' '}
                  {total.toLocaleString()}
                </span>
              )}
              {fetchDurationMs !== null && <span>Took {(fetchDurationMs / 1000).toFixed(2)}s</span>}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '12px',
                color: 'text.secondary',
              }}
            >
              <label htmlFor="pt-rows-per-page">Rows:</label>
              <SelectMenu
                id="pt-rows-per-page"
                minWidth={72}
                helperTextEnabled={false}
                selectedValue={String(pageSize)}
                items={[25, 50, 100].map((size) => ({ value: String(size), label: String(size) }))}
                onChange={(value) => {
                  setPageSize(Number(value));
                  setPage(0);
                }}
              />
            </Box>
          </Box>

          <PickTaskTable
            rows={pageRows}
            sortModel={sortModel}
            columnSelection={columnSelection}
            selectedIds={selectedIds}
            onSortChange={state.onSortChange}
            onReorder={state.onReorder}
            onToggleRow={state.onToggleRow}
            onToggleAll={state.onToggleAll}
            onSelectRange={state.onSelectRange}
            onRowClick={(id) => navigate(`/picktasks/${id}`)}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '20px',
              pb: 2,
            }}
          >
            <Box sx={{ width: 160 }} />
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              // Reset to the first page: a larger page size can put the current
              // index past the end, which would render an empty list.
              onPageSizeChange={(size: number) => {
                setPageSize(size);
                setPage(0);
              }}
            />
            <AutoRefresh interval={autoRefreshInterval} onChange={setAutoRefreshInterval} />
          </Box>
        </Box>
      </PageContainer>
      <ColumnSelection
        open={columnsOpen}
        title="Select Columns"
        value={columnSelection}
        onChange={setColumnSelection}
        onClose={() => setColumnsOpen(false)}
        onReset={state.resetColumnSelection}
      />
      <SaveReportDialog
        open={reports.saveDialogOpen}
        onClose={() => reports.setSaveDialogOpen(false)}
        onSaveNew={reports.handleSaveNewReport}
        onOverwrite={reports.handleOverwriteReport}
        activeReportName={reports.activeReportName}
      />
    </>
  );
};
